import React from 'react';

/**
 * Reusable TextArea Component.
 * Styles with Tailwind CSS, providing a multi-line text input.
 *
 * @param {object} props - Component props.
 * @param {string} [props.id] - ID for the textarea, also used for label's htmlFor.
 * @param {string} [props.name] - Name attribute for the textarea.
 * @param {string} [props.label] - Optional label text for the textarea.
 * @param {string} [props.placeholder=''] - Placeholder text.
 * @param {string} [props.value] - Controlled component value.
 * @param {function} [props.onChange] - Change event handler.
 * @param {boolean} [props.required=false] - If true, textarea is required.
 * @param {boolean} [props.disabled=false] - If true, textarea is disabled.
 * @param {number} [props.rows=3] - Number of visible rows in the textarea.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the textarea element.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label element.
 * @param {string} [props.containerClassName=''] - Additional custom Tailwind classes for the wrapper div.
 * @param {string} [props.error=''] - Error message to display below the textarea.
 */
const TextArea = ({
    id,
    name,
    label,
    placeholder = '',
    value,
    onChange,
    required = false,
    disabled = false,
    rows = 3, // Default rows for textarea
    className = '',
    labelClassName = '',
    containerClassName = '',
    error = '',
    ...props
}) => {
    const textareaBaseStyles = 'w-full px-4 py-2 border rounded-lg ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
        'transition-all duration-200 ease-in-out ' +
        'placeholder-gray-400 text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed';

    const errorStyles = error ? 'border-gmail-red' : 'border-gray-medium';

    return (
        <div className={`mb-4 ${containerClassName}`}>
            {label && (
                <label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1">*</span>}
                </label>
            )}
            <textarea
                id={id || name}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                disabled={disabled}
                rows={rows}
                className={`${textareaBaseStyles} ${errorStyles} ${className}`}
                {...props}
            />
            {error && <p className="text-gmail-red text-xs mt-1">{error}</p>}
        </div>
    );
};

export default TextArea;
